// A compact feed row for mobile: disc + title/producer/meta + stats + remix button.
// Tapping the row opens the seed; tapping the disc plays/pauses; tapping remix
// opens the DAW picker sheet (Start Jam instead, on the jam picker).

import { useMemo, useState } from "react";
import { Link } from "react-router";
import { GitBranch } from "lucide-react";
import type { FeedCard } from "@shared/api-types.js";
import { relativeTime } from "@web-shared/lib/format";
import { seedPath } from "@/lib/ctas";
import { usePlayer, useTrackStatus, type PlayerTrack } from "../player";
import { SeedCover } from "./SeedCover";
import { DawPicker } from "./DawPicker";
import { feedTrack } from "./FeedCardView";
import { RemixByline } from "./RemixCredit";
import { StartJamButton } from "./StartJamButton";
import { useFeedCta } from "./feed-cta";

export function FeedRow({
  card,
  queue,
}: {
  card: FeedCard;
  queue: PlayerTrack[];
}) {
  const track = useMemo(() => feedTrack(card), [card]);
  const status = useTrackStatus(track.id);
  const player = usePlayer();
  const playable = track.url !== "";
  const [showPicker, setShowPicker] = useState(false);
  const cta = useFeedCta();
  return (
    <>
      <article className="flex items-center gap-3 py-3 border-b border-white/5">
        <SeedCover
          seed={{ id: card.id, title: card.title, genre: card.genre, bpm: card.bpm, key: card.key, producer: card.producer.username }}
          spinning={status === "playing"}
          interactive={playable}
          playStyle="overlay"
          playing={status === "playing"}
          remixBadge={card.remixOf ? "pip" : undefined}
          onPlay={() => player.toggle(track, queue)}
          className="w-14 shrink-0"
        />
        <Link to={seedPath(card.id)} className="flex-1 min-w-0">
          <div className="font-bold truncate">{card.title}</div>
          <div className="text-xs text-muted-foreground truncate">
            {card.producer.username} · {relativeTime(card.createdAt)}
          </div>
          {/* Unlinked: this whole block is already inside a Link to the seed,
              and an anchor in an anchor is invalid. The seed page one tap away
              carries the linked credit. */}
          {card.remixOf && (
            <RemixByline origin={card.remixOf} linked={false} className="mt-0.5 text-[10px] text-primary/80" />
          )}
          <div className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground/50 mt-0.5">
            <span className="flex items-center gap-0.5">
              <GitBranch className="size-2.5" />
              {card.remixCount}
            </span>
            <span>·</span>
            {card.stemCount > 0 && <span>{card.stemCount} tracks</span>}
            {card.stemCount > 0 && <span>·</span>}
            <span>v{card.version}</span>
          </div>
        </Link>
        {cta === "jam" ? (
          <StartJamButton seedId={card.id} size="row" label="Start Jam" className="shrink-0" />
        ) : (
          <button
            type="button"
            onClick={() => setShowPicker(true)}
            className="h-8 px-3 rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0"
          >
            Remix
          </button>
        )}
      </article>
      {showPicker && (
        <DawPicker
          seedId={card.id}
          seedTitle={card.title}
          onClose={() => setShowPicker(false)}
        />
      )}
    </>
  );
}
